import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const SpectrumBlockPlaceEffect = ({ position, color, onComplete }) => {
  const groupRef = useRef();
  const startTime = useRef(Date.now());
  const sparklesRef = useRef();

  const sparkleCount = 12;
  const sparkleData = useMemo(() => {
    const data = [];
    for (let i = 0; i < sparkleCount; i++) {
      const angle = (i / sparkleCount) * Math.PI * 2;
      data.push({
        angle,
        speed: 2 + Math.random() * 2,
        size: 0.1 + Math.random() * 0.1,
      });
    }
    return data;
  }, []);

  useFrame(() => {
    if (!groupRef.current) return;

    const elapsed = (Date.now() - startTime.current) / 1000;
    const duration = 0.4;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    const progress = elapsed / duration;

    // Expand sparkles outward
    if (sparklesRef.current) {
      sparklesRef.current.children.forEach((sparkle, i) => {
        const data = sparkleData[i];
        const distance = data.speed * progress;
        sparkle.position.x = Math.cos(data.angle) * distance;
        sparkle.position.y = Math.sin(data.angle) * distance;
        sparkle.scale.setScalar(data.size * (1 - progress));
        sparkle.material.opacity = 1 - progress;
      });
    }

    // Flash effect
    if (groupRef.current.children[0]) {
      groupRef.current.children[0].material.opacity = (1 - progress) * 0.8;
      groupRef.current.children[0].scale.setScalar(1 + progress * 0.5);
    }
  });

  const baseColor = new THREE.Color(color);

  return (
    <group ref={groupRef} position={position}>
      {/* Central flash */}
      <mesh>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          color={baseColor}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Sparkles */}
      <group ref={sparklesRef}>
        {sparkleData.map((data, i) => (
          <mesh key={i}>
            <circleGeometry args={[data.size, 6]} />
            <meshBasicMaterial
              color={0xffffff}
              transparent
              opacity={1}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
};

export default SpectrumBlockPlaceEffect;
